import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Review } from "../../types/ReviewTypes";
import { authFetch } from "../../utils/authUtils";
import { encodeId } from "../../components/HelperMethods";
import ReviewForm from "../../components/Review/ReviewForm";
import StarRating from "../../components/StarRating";
import "../../components/StarRating.css";
import "./ReviewPage.css";
import TextBox from "../../components/Common/TextBox";
// import "../Song/Song.css";

interface SongInfo {
    song_id: number;
    song_name: string;
    artist_ids: number[];
    artist_names: string[];
    album_id: number;
    album_english_name: string;
    s3_picture_url: string;
}

const ReviewPage: React.FC = () => {
    const { reviewId } = useParams<{ reviewId: string }>();
    const [review, setReview] = useState<Review | null>(null);
    const [songInfo, setSongInfo] = useState<SongInfo | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchReviewData = async () => {
            setIsLoading(true);
            setError(null);

            try {
                // Fetch the review details
                const reviewResponse = await authFetch(`/api/reviews/${reviewId}`);
                if (!reviewResponse.ok) {
                    throw new Error(`Error ${reviewResponse.status}: ${await reviewResponse.text()}`);
                }

                const reviewData = await reviewResponse.json();
                setReview(reviewData);

                // Fetch the song details
                const songResponse = await fetch(`/api/songs/details/${reviewData.song_id}`);
                if (!songResponse.ok) {
                    throw new Error(`Error fetching song: ${songResponse.status}`);
                }

                const songData = await songResponse.json();
                setSongInfo({
                    song_id: reviewData.song_id,
                    song_name: songData.english_name,
                    artist_ids: songData.artist_ids,
                    artist_names: songData.artist_names,
                    album_id: songData.melon_album_id,
                    album_english_name: songData.album_english_name,
                    s3_picture_url: songData.s3_picture_url
                });

                console.log(songData);
            } catch (err) {
                console.error("Error fetching review data:", err);
                setError(err instanceof Error ? err.message : String(err));
            } finally {
                setIsLoading(false);
            }
        };

        if (reviewId) {
            fetchReviewData();
        }
    }, [reviewId]);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const handleReviewUpdated = (updatedReview: Review) => {
        setReview({
            ...review!,
            rating: updatedReview.rating,
            content: updatedReview.content,
            updated_at: updatedReview.updated_at
        });
    };

    if (isLoading) {
        return <div className="loading-container">Loading review...</div>;
    }

    if (error || !review) {
        return (
            <div className="error-container">
                <h3>Error loading review</h3>
                <p>{error || "Review not found"}</p>
                <button onClick={() => navigate(-1)}>Go Back</button>
            </div>
        );
    }

    return (
        <div className="review-page-container">
            <Helmet>
                <title>Review of {songInfo?.song_name || "Song"} - dubu</title>
                <meta name="description" content={`Review of ${songInfo?.song_name} by ${songInfo?.artist_names.join(", ")}`} />
            </Helmet>

            <div className="song-header-review">
                <div className="song-album-cover">
                    <img
                        src={songInfo?.s3_picture_url || "/dubuFace.jpg"}
                        alt={songInfo?.song_name}
                        onError={(e) => {
                            e.currentTarget.src = "/dubuFace.jpg";
                            e.currentTarget.onerror = null;
                        }}
                    />
                </div>
                <div className="song-info-review">
                    <h5 className="album-name-song-page">
                        SONG ON{" "}
                        {songInfo?.song_id && songInfo.album_id && (
                            <Link to={`/album/${encodeId(songInfo.album_id)}`} state={{ id: songInfo.album_id }} className="a-name-link">
                                {songInfo.album_english_name?.toUpperCase()}
                            </Link>
                        )}
                    </h5>
                    <div className="main-song-text">
                        <h1 className="song-name">
                            <Link to={`/song/${encodeId(review.song_id)}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                                {songInfo?.song_name}
                            </Link>
                        </h1>
                    </div>
                    <div className="artist-credits-list">
                        <div className="main-performer-list">
                            by
                            {songInfo?.artist_ids && songInfo.artist_names &&
                                songInfo.artist_ids.map((artistId, index) => (
                                    <h4 className="artist-credit-link" key={artistId}>
                                        <Link
                                            to={`/artist/${encodeId(artistId)}`}
                                            state={{ id: artistId }}
                                        >
                                            {artistId !== -1 && songInfo.artist_names[index]}
                                        </Link>
                                        {artistId === -1 && songInfo.artist_names[index]}
                                    </h4>
                                ))}
                        </div>
                    </div>
                    <h5 className="album-name-song-page-mobile" style={{ "marginBottom": "0em" }}>
                        SONG ON{" "}
                        {songInfo?.song_id && songInfo.album_id && (
                            <Link to={`/album/${encodeId(songInfo.album_id)}`} state={{ id: songInfo.album_id }} className="album-link-mobile">
                                {songInfo.album_english_name?.toUpperCase()}
                            </Link>
                        )}
                    </h5>
                </div>
            </div>

            <TextBox
                picture={review.picture}
                username={review.dubu_user}
                content={review.content}
                createdAt={review.created_at}
                updatedAt={review.updated_at}
                rating={Number(review.rating)}
                isOwner={review.is_owner}
                onEdit={() => setIsEditModalOpen(true)}
                defaultAvatar="/dubuFace.jpg"
            />

            <div className="comments-section">
                <div className="no-comments">
                    <p>Comment feature coming soon!</p>
                </div>
            </div>

            {review && songInfo && (
                <ReviewForm
                    songId={songInfo.song_id}
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    existingReview={review}
                    onReviewAdded={() => { }}
                    onReviewUpdated={handleReviewUpdated}
                />
            )}
        </div>
    );
};

export default ReviewPage; 